import type {
  PatientRelationName,
  PatientRelations,
  PatientWithRelations,
} from '@app/icare/entities/patient/schema/Patient'
import type { SimpleRelationValidPeriod } from '@app/icare/entities/patient/schema/PatientRelation'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter.js'

dayjs.extend(isSameOrAfter)

interface IsActiveOpts {
  /**
   * Whether to consider the relation as active if it has no activeTo date
   */
  allowNoActiveTo?: boolean

  /**
   * Whether to consider the relation as active if it has no activeFrom date
   */
  allowNoActiveFrom?: boolean
}

export function isActivePatientRelation(
  rel?: SimpleRelationValidPeriod | null,
  opts: IsActiveOpts | undefined | number = {},
) {
  // Type number for opts is just a convience method so we can use it in array.filter
  const normalizedOpts = typeof opts === 'number' ? { allowNoActiveTo: undefined, allowNoActiveFrom: undefined } : opts
  const { allowNoActiveTo = true, allowNoActiveFrom = true } = normalizedOpts
  if (!rel) {
    return false
  }
  if (!rel.activeTo && !allowNoActiveTo) {
    return false
  }
  if (!rel.activeFrom && !allowNoActiveFrom) {
    return false
  }

  if (rel && !rel.activeTo) {
    return true
  }
  return dayjs(rel.activeTo).isSameOrAfter(new Date(), 'day')
}

export function sortByActiveRange(a?: SimpleRelationValidPeriod, b?: SimpleRelationValidPeriod) {
  if (!a || !b) {
    return 0
  }
  if (!a.activeFrom || !b.activeFrom) {
    return 0
  }
  return dayjs(a.activeFrom).diff(dayjs(b.activeFrom))
}

export function isInactivePatientRelation(rel?: SimpleRelationValidPeriod | null, opts: IsActiveOpts | number = {}) {
  return !isActivePatientRelation(rel, opts)
}

export function isActivePrescriber(prescriber?: SimpleRelationValidPeriod | null) {
  return isActivePatientRelation(prescriber, { allowNoActiveFrom: false })
}

export function sortByRecent(a: SimpleRelationValidPeriod, b: SimpleRelationValidPeriod) {
  if (!a.activeFrom || !b.activeFrom) {
    return 0
  }
  if (!a.activeFrom && b.activeFrom) {
    return 1
  }
  if (a.activeFrom && !b.activeFrom) {
    return -1
  }

  return dayjs(b.activeFrom).diff(dayjs(a.activeFrom))
}
